/** Einheiten: intern rechnen wir in Metern, angezeigt/ eingegeben wird mm, cm oder m. */

export type Unit = 'mm' | 'cm' | 'm';

export const UNIT_LABEL: Record<Unit, string> = { mm: 'mm', cm: 'cm', m: 'm' };
/** Faktor Einheit → Meter. */
const TO_METER: Record<Unit, number> = { mm: 0.001, cm: 0.01, m: 1 };

export function toMeter(value: number, unit: Unit): number {
  return value * TO_METER[unit];
}

export function fromMeter(value: number, unit: Unit): number {
  return value / TO_METER[unit];
}

/**
 * Parst eine Maßangabe wie "120", "1200mm", "1,5m", "3m" → Meter.
 * Dezimalkomma und Dezimalpunkt werden akzeptiert.
 */
export function parseLength(input: string, fallbackUnit: Unit = 'cm'): number | null {
  const s = input.trim().toLowerCase().replace(',', '.');
  if (s.length === 0) return null;
  const match = s.match(/^(-?\d*\.?\d+)\s*(mm|cm|m|'|ft|"|in)?$/);
  if (!match) return null;
  const value = parseFloat(match[1]!);
  if (!Number.isFinite(value)) return null;
  switch (match[2]) {
    case 'mm': return value * 0.001;
    case 'cm': return value * 0.01;
    case 'm': return value;
    case "'":
    case 'ft': return value * 0.3048;
    case '"':
    case 'in': return value * 0.0254;
    default: return toMeter(value, fallbackUnit);
  }
}

/** Parst einen Winkel wie "30", "45,5°" → Grad. */
export function parseAngle(input: string): number | null {
  const s = input.trim().toLowerCase().replace(',', '.').replace('°', '').replace('deg', '');
  const match = s.match(/^(-?\d*\.?\d+)$/);
  if (!match) return null;
  const value = parseFloat(match[1]!);
  return Number.isFinite(value) ? value : null;
}

/**
 * Parst eine VCB-Eingabe in eine Liste von Zahlen (Meter bzw. Grad, je nach `unitKind`).
 * Wie im deutschen SketchUp: Listentrenner ist das Semikolon ("120;60"), das Komma ist
 * Dezimalzeichen ("1,5m"). Ohne Semikolon, aber mit mehreren Kommas ("10,20,30"),
 * gelten die Kommas als Trenner (eine Zahl kann nicht zwei Dezimalkommas haben).
 */
export function parseMeasureList(
  input: string,
  unitKind: 'length' | 'angle',
  fallbackUnit: Unit = 'cm'
): number[] | null {
  const commas = (input.match(/,/g) ?? []).length;
  const sep = input.includes(';') ? /;/ : commas >= 2 ? /,/ : /;/;
  const parts = input.split(sep).map((p) => p.trim()).filter((p) => p.length > 0);
  if (parts.length === 0) return null;
  const out: number[] = [];
  for (const part of parts) {
    const v = unitKind === 'length' ? parseLength(part, fallbackUnit) : parseAngle(part);
    if (v === null) return null;
    out.push(v);
  }
  return out;
}

/** Formatiert Meter in der Zieleinheit mit sinnvollen Nachkommastellen. */
export function formatLength(meters: number, unit: Unit): string {
  const v = fromMeter(meters, unit);
  const decimals = unit === 'm' ? 3 : unit === 'cm' ? 1 : 0;
  let text = v.toFixed(decimals);
  if (text.includes('.')) text = text.replace(/\.?0+$/, '');
  return `${text} ${UNIT_LABEL[unit]}`;
}

export function formatAngle(deg: number): string {
  return `${Math.round(deg * 10) / 10}°`;
}
